import dayjs from 'dayjs'

export function isClientTask (task) {
  return task.type != null && String(task.type).toLowerCase() === 'client'
}

export function canMarkTask (task) {
  return isClientTask(task) && !task.isClosed
}

export function isTaskCompleted (task) {
  return !!(task.clientMarkedCompleted || task.isClosed)
}

export function getTaskStatus (task) {
  if (isTaskCompleted(task)) return 'completed'
  if (task.dueDate && dayjs(task.dueDate).isBefore(dayjs(), 'day')) return 'overdue'
  return 'outstanding'
}

export function formatTaskDate (isoDate) {
  if (!isoDate) return null
  return dayjs(isoDate).format('M/D/YYYY')
}

export function formatTaskDateLong (isoDate) {
  if (!isoDate) return null
  return dayjs(isoDate).format('ddd, MMM D, YYYY')
}

const dateValue = (task, field) => task[field] ? dayjs(task[field]).valueOf() : null
const stringValue = (task, field) => task[field] == null ? null : String(task[field]).toLowerCase()
const boolValue = (task, field) => task[field] ? 1 : 0

export const SORT_ACCESSORS = {
  type: (t) => stringValue(t, 'type'),
  subject: (t) => stringValue(t, 'subject'),
  dueDate: (t) => dateValue(t, 'dueDate'),
  priority: (t) => stringValue(t, 'priority'),
  createdDate: (t) => dateValue(t, 'createdDate'),
  completedDate: (t) => dateValue(t, 'completedDate'),
  description: (t) => stringValue(t, 'description'),
  codeName: (t) => stringValue(t, 'codeName'),
  clientMarkedCompleted: (t) => boolValue(t, 'clientMarkedCompleted'),
  isHighPriority: (t) => boolValue(t, 'isHighPriority'),
  isClosed: (t) => boolValue(t, 'isClosed'),
  id: (t) => stringValue(t, 'id')
}

export function isColumnSortable (key) {
  return Object.prototype.hasOwnProperty.call(SORT_ACCESSORS, key)
}

// Nullish always sorts last regardless of direction.
function compareValues (a, b, direction) {
  if (a == null && b == null) return 0
  if (a == null) return 1
  if (b == null) return -1
  const cmp = typeof a === 'number' && typeof b === 'number'
    ? a - b
    : String(a).localeCompare(String(b))
  return direction === 'desc' ? -cmp : cmp
}

// Default sort applied when the user hasn't explicitly chosen a column.
// Customer-requested behavior:
//  - open:   due date ascending (oldest first)
//  - closed: completed date descending (newest first)
//  - all:    open tasks first (by due date asc), then closed tasks (by completed date desc)
function compareDefault (a, b, statusFilter) {
  if (statusFilter === 'closed') {
    return compareValues(SORT_ACCESSORS.completedDate(a), SORT_ACCESSORS.completedDate(b), 'desc')
  }
  if (statusFilter === 'all') {
    const closedA = isTaskCompleted(a) ? 1 : 0
    const closedB = isTaskCompleted(b) ? 1 : 0
    if (closedA !== closedB) return closedA - closedB
    if (closedA === 1) {
      return compareValues(SORT_ACCESSORS.completedDate(a), SORT_ACCESSORS.completedDate(b), 'desc')
    }
  }
  return compareValues(SORT_ACCESSORS.dueDate(a), SORT_ACCESSORS.dueDate(b), 'asc')
}

export function sortTasks (tasks, sortState, statusFilter) {
  if (sortState?.key && SORT_ACCESSORS[sortState.key]) {
    const accessor = SORT_ACCESSORS[sortState.key]
    const direction = sortState.direction === 'desc' ? 'desc' : 'asc'
    return [...tasks].sort((a, b) => compareValues(accessor(a), accessor(b), direction))
  }
  return [...tasks].sort((a, b) => compareDefault(a, b, statusFilter))
}

export function applyFilters (tasks, config, sortState, statusFilter) {
  let filtered = tasks

  if (Array.isArray(config?.filterIds)) {
    const idSet = new Set(config.filterIds.map(String))
    filtered = filtered.filter(t => idSet.has(String(t.id)))
  }

  filtered = sortTasks(filtered, sortState, statusFilter)

  const limit = config?.limit
  if (limit && limit > 0) {
    filtered = filtered.slice(0, limit)
  }

  return filtered
}
